import { supabase } from '../../supabase';
import { useQuery, useMutation } from '@tanstack/react-query';
import { GGSwapShift } from '@shiftly/shared/src/schema';
import { DateTime } from 'luxon';

export interface GGSwapShiftFilter {
  field: keyof GGSwapShift | string;
  operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'not-in' | 'is';
  value: any;
}

export interface GGSwapShiftSort {
  field: keyof GGSwapShift | string;
  direction: 'asc' | 'desc';
}

export interface GGSwapShiftParams {
  filters?: GGSwapShiftFilter[];
  sort?: GGSwapShiftSort[];
  enabled?: boolean;
}

const normalizeDateTo12PM = (date: DateTime): DateTime => {
  const normalized = date.set({ hour: 12, minute: 0, second: 0, millisecond: 0});
  return normalized;
};

export function convertDateFields(data: any): GGSwapShift {
  return {
    ...data,
    date: DateTime.fromISO(data.date, { zone: 'UTC' }),
    created_at: DateTime.fromISO(data.created_at, { zone: 'UTC' }),
    updated_at: DateTime.fromISO(data.updated_at, { zone: 'UTC'})
  } as GGSwapShift;
}

class SupabaseGGSwapShiftService {
  async getGGSwapShifts({
    filters = [],
    sort = []
  }: GGSwapShiftParams): Promise<GGSwapShift[]> {
    try {
      const inFilters = filters.filter(f => f.operator === 'in');
      const largeInFilter = inFilters.find(f => Array.isArray(f.value) && f.value.length > 10);

      if (largeInFilter && Array.isArray(largeInFilter.value)) {
        // Optimized batching for large 'in' queries
        const CHUNK_SIZE = 100;
        const MAX_CONCURRENT_QUERIES = 10;

        const values = largeInFilter.value;
        const chunks = Array.from(
          { length: Math.ceil(values.length / CHUNK_SIZE) },
          (_, i) => values.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)
        );

        const processChunk = async (chunkValues: any[]): Promise<GGSwapShift[]> => {
          // Create filters for this chunk
          const chunkFilters = filters.map(f =>
            f === largeInFilter ? { ...f, value: chunkValues } : f
          );

          // Get total count first to optimize pagination
          let countQuery = supabase.from('gg_swap_shifts').select('*', { count: 'exact', head: true });
          chunkFilters.forEach(filter => {
            switch (filter.operator) {
              case 'eq': countQuery = countQuery.eq(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
              case 'neq': countQuery = countQuery.neq(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
              case 'gt': countQuery = countQuery.gt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
              case 'gte': countQuery = countQuery.gte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
              case 'lt': countQuery = countQuery.lt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
              case 'lte': countQuery = countQuery.lte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
              case 'in': countQuery = countQuery.in(filter.field, filter.value); break;
              case 'not-in': countQuery = countQuery.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`); break;
              case 'is': countQuery = countQuery.is(filter.field, filter.value); break;
            }
          });

          const { count } = await countQuery;
          if (!count || count === 0) return [];

          // Calculate optimal page count for parallel requests
          const totalPages = Math.ceil(count / SUPABASE_PAGE_SIZE);
          const pagePromises: Promise<GGSwapShift[]>[] = [];

          // Create all page requests in parallel
          for (let page = 0; page < totalPages; page++) {
            const pagePromise = (async (offset: number): Promise<GGSwapShift[]> => {
              let query = supabase.from('gg_swap_shifts').select('*');

              // Apply filters
              chunkFilters.forEach(filter => {
                switch (filter.operator) {
                  case 'eq':
                    query = query.eq(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'neq':
                    query = query.neq(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'gt':
                    query = query.gt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'gte':
                    query = query.gte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'lt':
                    query = query.lt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'lte':
                    query = query.lte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'in':
                    query = query.in(filter.field, filter.value);
                    break;
                  case 'not-in':
                    query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
                    break;
                  case 'is':
                    query = query.is(filter.field, filter.value);
                    break;
                }
              });

              // Apply sorting with stable secondary sort
              if (sort.length > 0) {
                sort.forEach(sortOption => {
                  query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
                });
                // Add stable secondary sort if not already sorting by id
                if (!sort.some(s => s.field === 'id')) {
                  query = query.order('id', { ascending: true });
                }
              } else {
                query = query.order('date', { ascending: true }).order('id', { ascending: true });
              }

              // Add pagination
              query = query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

              const { data, error } = await query;

              if (error) throw error;

              return (data || []) as GGSwapShift[];
            })(page * SUPABASE_PAGE_SIZE);

            pagePromises.push(pagePromise);
          }

          // Execute all page requests in parallel
          const pageResults = await Promise.all(pagePromises);
          return pageResults.flat();
        };

        // Process chunks with optimized concurrency
        const results: GGSwapShift[] = [];

        for (let i = 0; i < chunks.length; i += MAX_CONCURRENT_QUERIES) {
          const currentBatch = chunks.slice(i, i + MAX_CONCURRENT_QUERIES);
          const batchPromises = currentBatch.map(processChunk);

          const batchResults = await Promise.all(batchPromises);
          results.push(...batchResults.flat());
        }

        return results.map((shift: GGSwapShift) => convertDateFields(shift));
      }

      // Standard pagination for normal queries
      const SUPABASE_PAGE_SIZE = 1000;
      const MAX_CONCURRENT_PAGES = 10;

      // Get total count first
      let countQuery = supabase.from('gg_swap_shifts').select('*', { count: 'exact', head: true });
      filters.forEach(filter => {
        switch (filter.operator) {
          case 'eq': countQuery = countQuery.eq(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
          case 'neq': countQuery = countQuery.neq(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
          case 'gt': countQuery = countQuery.gt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
          case 'gte': countQuery = countQuery.gte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
          case 'lt': countQuery = countQuery.lt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
          case 'lte': countQuery = countQuery.lte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value); break;
          case 'in': countQuery = countQuery.in(filter.field, filter.value); break;
          case 'not-in': countQuery = countQuery.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`); break;
          case 'is': countQuery = countQuery.is(filter.field, filter.value); break;
        }
      });

      const { count } = await countQuery;
      if (!count || count === 0) return [];

      const totalPages = Math.ceil(count / SUPABASE_PAGE_SIZE);

      // For large datasets, use parallel pagination
      if (totalPages > 5) {
        const allResults: GGSwapShift[] = [];

        for (let batchStart = 0; batchStart < totalPages; batchStart += MAX_CONCURRENT_PAGES) {
          const batchEnd = Math.min(batchStart + MAX_CONCURRENT_PAGES, totalPages);
          const pagePromises: Promise<GGSwapShift[]>[] = [];

          for (let page = batchStart; page < batchEnd; page++) {
            const pagePromise = (async (offset: number): Promise<GGSwapShift[]> => {
              let query = supabase.from('gg_swap_shifts').select('*');

              // Apply filters
              filters.forEach(filter => {
                switch (filter.operator) {
                  case 'eq':
                    query = query.eq(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'neq':
                    query = query.neq(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'gt':
                    query = query.gt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'gte':
                    query = query.gte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'lt':
                    query = query.lt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'lte':
                    query = query.lte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                    break;
                  case 'in':
                    query = query.in(filter.field, filter.value);
                    break;
                  case 'not-in':
                    query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
                    break;
                  case 'is':
                    query = query.is(filter.field, filter.value);
                    break;
                }
              });

              // Apply sorting with stable secondary sort
              if (sort.length > 0) {
                sort.forEach(sortOption => {
                  query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
                });
                // Add stable secondary sort if not already sorting by id
                if (!sort.some(s => s.field === 'id')) {
                  query = query.order('id', { ascending: true });
                }
              } else {
                query = query.order('date', { ascending: true }).order('id', { ascending: true });
              }

              // Add pagination
              query = query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

              const { data, error } = await query;
              if (error) throw error;

              return (data || []) as GGSwapShift[];
            })(page * SUPABASE_PAGE_SIZE);

            pagePromises.push(pagePromise);
          }

          // Execute batch in parallel
          const batchResults = await Promise.all(pagePromises);
          allResults.push(...batchResults.flat());
        }

        return allResults.map((shift: GGSwapShift) => convertDateFields(shift));
      } else {
        // Use sequential pagination for small datasets
        const allResults: GGSwapShift[] = [];
        let hasMore = true;
        let offset = 0;

        while (hasMore) {
          let query = supabase.from('gg_swap_shifts').select('*');

          // Apply filters
          filters.forEach(filter => {
            switch (filter.operator) {
              case 'eq':
                query = query.eq(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                break;
              case 'neq':
                query = query.neq(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                break;
              case 'gt':
                query = query.gt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                break;
              case 'gte':
                query = query.gte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                break;
              case 'lt':
                query = query.lt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                break;
              case 'lte':
                query = query.lte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                break;
              case 'in':
                query = query.in(filter.field, filter.value);
                break;
              case 'not-in':
                query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
                break;
              case 'is':
                query = query.is(filter.field, filter.value);
                break;
            }
          });

          // Apply sorting with stable secondary sort
          if (sort.length > 0) {
            sort.forEach(sortOption => {
              query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
            });
            // Add stable secondary sort if not already sorting by id
            if (!sort.some(s => s.field === 'id')) {
              query = query.order('id', { ascending: true });
            }
          } else {
            query = query.order('date', { ascending: true }).order('id', { ascending: true });
          }

          // Add pagination
          query = query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

          const { data, error } = await query;

          if (error) {
            console.error('Error fetching GG swap shifts page:', error);
            throw error;
          }

          const shifts = (data || []) as GGSwapShift[];
          allResults.push(...shifts);

          // Check if we got fewer results than requested (end of data)
          hasMore = shifts.length === SUPABASE_PAGE_SIZE;
          offset += SUPABASE_PAGE_SIZE;
        }

        return allResults.map((shift: GGSwapShift) => convertDateFields(shift));
      }
    } catch (error) {
      console.error('Error in getGGSwapShifts:', error);
      return [];
    }
  }

  async createGGSwapShift(shift: Omit<GGSwapShift, 'id' | 'created_at' | 'updated_at'>): Promise<string> {
    try {
      const now = DateTime.utc();
      const normalizedDate = normalizeDateTo12PM(shift.date);
      
      const { data, error } = await supabase
        .from('gg_swap_shifts')
        .insert({
          user_id: shift.user_id,
          model_id: shift.model_id,
          date: normalizedDate.toISO(),
          is_cover: shift.is_cover ?? false,
          created_at: now.toISO(),
          updated_at: now.toISO()
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating GG swap shift:', error);
        throw error;
      }

      return data.id;
    } catch (error) {
      console.error('Error in createGGSwapShift:', error);
      throw error;
    }
  }

  async updateGGSwapShift(shiftId: string, data: Partial<GGSwapShift>): Promise<void> {
    try {
      const updated_data: any = {
        updated_at: DateTime.utc().toISO()
      };

      Object.entries(data).forEach(([key, value]) => {
        if (key === 'id' || key === 'created_at' || key === 'updated_at') return;

        if (key === 'date' && value instanceof DateTime) {
          updated_data[key] = normalizeDateTo12PM(value).toISO();
        } else if (value instanceof DateTime) {
          updated_data[key] = value.toISO();
        } else {
          updated_data[key] = value;
        }
      });

      const { error } = await supabase
        .from('gg_swap_shifts')
        .update(updated_data)
        .eq('id', shiftId);

      if (error) {
        console.error('Error updating GG swap shift:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in updateGGSwapShift:', error);
      throw error;
    }
  }

  async deleteGGSwapShift(shiftId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('gg_swap_shifts')
        .delete()
        .eq('id', shiftId);

      if (error) {
        console.error('Error deleting GG swap shift:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteGGSwapShift:', error);
      throw error;
    }
  }

  async getGGSwapShiftsForMonth(month: DateTime): Promise<GGSwapShift[]> {
    try {
      const monthStart = month.startOf('month');
      const monthEnd = month.endOf('month');

      const filters: GGSwapShiftFilter[] = [
        { field: 'date', operator: 'gte', value: monthStart.toISO() },
        { field: 'date', operator: 'lte', value: monthEnd.toISO() }
      ];

      return this.getGGSwapShifts({ filters });
    } catch (error) {
      console.error('Error in getGGSwapShiftsForMonth:', error);
      return [];
    }
  }

  async bulkCreateGGSwapShifts(shifts: Omit<GGSwapShift, 'id' | 'created_at' | 'updated_at'>[]): Promise<string[]> {
    try {
      const now = DateTime.utc();
      const insertData = shifts.map(shift => ({
        user_id: shift.user_id,
        model_id: shift.model_id,
        date: normalizeDateTo12PM(shift.date).toISO(),
        is_cover: shift.is_cover ?? false,
        created_at: now.toISO(),
        updated_at: now.toISO()
      }));

      // Process in batches of 1000 (Supabase limit)
      const batchSize = 1000;
      const createdIds: string[] = [];
      
      for (let i = 0; i < insertData.length; i += batchSize) {
        const batch = insertData.slice(i, i + batchSize);
        
        const { data, error } = await supabase
          .from('gg_swap_shifts')
          .insert(batch)
          .select('id');

        if (error) {
          console.error('Error in bulk create batch:', error);
          throw error;
        }

        if (data) {
          createdIds.push(...data.map(item => item.id));
        }
      }

      return createdIds;
    } catch (error) {
      console.error('Error in bulkCreateGGSwapShifts:', error);
      return [];
    }
  }
}

const instance = new SupabaseGGSwapShiftService();

// React Query Hooks
export const useGGSwapShifts = (params: GGSwapShiftParams = {}) => {
  return useQuery({
    queryKey: ['supabase-gg-swap-shifts', params],
    queryFn: () => instance.getGGSwapShifts(params),
    staleTime: 5 * 60 * 1000,
    enabled: params.enabled !== false
  });
};

export const useCreateGGSwapShift = () => {
  return useMutation({
    mutationFn: (shift: Omit<GGSwapShift, 'id' | 'created_at' | 'updated_at'>) => 
      instance.createGGSwapShift(shift),
  });
};

export const useDeleteGGSwapShift = () => {
  return useMutation({
    mutationFn: (params: string | { shiftId: string; date?: DateTime }) => {
      const shiftId = typeof params === 'string' ? params : params.shiftId;
      return instance.deleteGGSwapShift(shiftId);
    },
  });
};

export const useUpdateGGSwapShift = () => {
  return useMutation({
    mutationFn: ({ shiftId, data }: { shiftId: string; data: Partial<GGSwapShift>; date?: DateTime }) => 
      instance.updateGGSwapShift(shiftId, data),
  });
};

export const useBulkCreateGGSwapShifts = () => {
  return useMutation({
    mutationFn: (shifts: Omit<GGSwapShift, 'id' | 'created_at' | 'updated_at'>[]) => 
      instance.bulkCreateGGSwapShifts(shifts),
  });
};

export const useGGSwapShiftsForMonth = (month: DateTime) => {
  const monthStart = month.startOf('month');
  const monthEnd = month.endOf('month');

  const filters: GGSwapShiftFilter[] = [
    { field: 'date', operator: 'gte', value: monthStart.toISO() },
    { field: 'date', operator: 'lte', value: monthEnd.toISO() }
  ];

  const params: GGSwapShiftParams = { filters };

  return useQuery({
    queryKey: ['supabase-gg-swap-shifts', params],
    queryFn: () => instance.getGGSwapShiftsForMonth(month),
    staleTime: 60 * 1000,
  });
};

export default instance;